(() => {
  if (customElements.get('kik-quote-modal')) return;

  const jotformHosts = new Set(['form.jotform.com', 'eu.jotform.com', 'www.jotform.com', 'jotform.com']);
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  class KikQuoteModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function' || this.controller) return;

      this.section = this.closest('.shopify-section');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.section.addEventListener('click', (event) => this.onTrigger(event), options);
      this.querySelectorAll('[data-quote-close]').forEach((button) => {
        button.addEventListener('click', () => this.dialog.close(), options);
      });
      this.querySelector('[data-quote-retry]').addEventListener('click', () => this.loadFrame(), options);
      this.dialog.addEventListener('close', () => this.onClose(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dialog.close();
      }, options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPress = this.isBackdrop(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropPress && this.isBackdrop(event)) this.dialog.close();
        this.backdropPress = false;
      }, options);
      window.addEventListener('message', (event) => this.onMessage(event), options);
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.onClose();
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.loadTimeout);
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    buildUrl(link) {
      const url = new URL(link.dataset.quoteUrl || link.href);
      if (url.protocol !== 'https:' || !jotformHosts.has(url.hostname) || !/^\/\d+\/?$/.test(url.pathname)) return null;

      const product = link.closest('product-rerender') || this.section;
      const variantScript = product.querySelector('variant-picker script[data-variant]');
      let variant;
      try { variant = JSON.parse(variantScript?.textContent || 'null'); } catch (_) { /* Keep Liquid's prefill. */ }
      const selected = product.querySelector('select[name="id"] option:checked');
      const sku = variant?.sku ?? selected?.dataset.sku;
      const color = variant && link.dataset.colorOptionPosition
        ? variant['option' + link.dataset.colorOptionPosition]
        : product.querySelector('.variant-color-selected span')?.textContent.trim();
      if (sku !== undefined) {
        url.searchParams.delete('sku');
        if (sku) url.searchParams.set('sku', sku);
      }
      if (color !== undefined) {
        url.searchParams.delete('color');
        if (color) url.searchParams.set('color', color);
      }

      // Forward only known campaign fields, never arbitrary URL parameters or customer data.
      const pageParams = new URLSearchParams(window.location.search);
      campaignKeys.forEach((key) => {
        if (pageParams.has(key)) url.searchParams.set(key, pageParams.get(key));
      });
      url.searchParams.set('source_page', window.location.origin + window.location.pathname);
      url.searchParams.set('isIframeEmbed', '1');
      return url;
    }

    onTrigger(event) {
      const link = event.target.closest('.kik-quote-link');
      if (!link || event.defaultPrevented || event.button !== 0) return;
      // Quote controls are buttons, but prevent immediately so delegated handlers can
      // never turn a rapid click into navigation while the form is initializing.
      event.preventDefault();
      let url;
      try { url = this.buildUrl(link); } catch (_) { return; }
      if (!url) return;

      // A second click on the same open quote must not replace the iframe or emit a
      // duplicate open event. This also protects slower Jotform connections.
      if (this.dialog.open && this.activeUrl?.href === url.href && !this.submitted) return;

      this.trigger = link;
      const needsLoad = !this.frame || this.activeUrl?.href !== url.href || this.submitted;
      this.activeUrl = url;
      this.quoteKind = link.dataset.quoteKind || 'custom';
      const description = this.quoteKind === 'patches' ? 'Patches' : 'Embroidery / DTF';
      this.querySelector('[data-quote-selection]').textContent = [description, url.searchParams.get('color')].filter(Boolean).join(' · ');
      const fallback = new URL(url);
      fallback.searchParams.delete('isIframeEmbed');
      this.querySelector('[data-quote-fallback]').href = fallback.href;

      if (!this.dialog.open) {
        this.scrollState = {
          x: window.scrollX, y: window.scrollY,
          position: document.body.style.position, top: document.body.style.top,
          width: document.body.style.width, overflow: document.documentElement.style.overflow
        };
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollState.y}px`;
        document.body.style.width = '100%';
        this.querySelector('[data-quote-close]').focus({ preventScroll: true });
      }
      if (needsLoad) this.loadFrame();
      this.track('quote_form_open');
    }

    loadFrame() {
      clearTimeout(this.loadTimeout);
      this.submitted = false;
      this.querySelector('[data-quote-success]').hidden = true;
      this.querySelector('[data-quote-frame]').hidden = false;
      this.querySelector('[data-quote-loading]').hidden = false;
      this.querySelector('[data-quote-status]').textContent = 'Loading your quote form…';
      this.querySelector('[data-quote-retry]').hidden = true;
      this.querySelector('[data-quote-body]').setAttribute('aria-busy', 'true');
      const frame = document.createElement('iframe');
      frame.title = this.quoteKind === 'patches' ? 'Request a Quote — Patches' : 'Request a Quote — Embroidery / DTF';
      frame.src = this.activeUrl.href;
      frame.fetchPriority = 'high';
      frame.addEventListener('load', () => {
        if (frame !== this.frame) return;
        clearTimeout(this.loadTimeout);
        this.querySelector('[data-quote-loading]').hidden = true;
        this.querySelector('[data-quote-body]').setAttribute('aria-busy', 'false');
      });
      frame.addEventListener('error', () => { if (frame === this.frame) this.showLoadError(); });
      this.frame = frame;
      this.querySelector('[data-quote-frame]').replaceChildren(frame);
      this.loadTimeout = setTimeout(() => this.showLoadError(), 20000);
    }

    showLoadError() {
      this.querySelector('[data-quote-status]').textContent = 'The form is taking longer than expected. Try again or open it in a new tab.';
      this.querySelector('[data-quote-retry]').hidden = false;
      this.querySelector('[data-quote-body]').setAttribute('aria-busy', 'false');
    }

    onClose() {
      if (!this.scrollState) return;
      const state = this.scrollState;
      this.scrollState = null;
      document.body.style.position = state.position;
      document.body.style.top = state.top;
      document.body.style.width = state.width;
      document.documentElement.style.overflow = state.overflow;
      window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    onMessage(event) {
      if (!this.frame || !this.activeUrl || event.source !== this.frame.contentWindow || this.submitted) return;
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (_) { return; }
      }
      if (!data || typeof data !== 'object') return;

      const jotformOrigins = new Set([this.activeUrl.origin, 'https://submit.jotform.com', 'https://www.jotform.com', 'https://jotform.com']);
      const jotformSuccess = jotformOrigins.has(event.origin) && data.action === 'submission-completed';
      // Our same-origin thank-you page relays success instead of tracking inside the iframe.
      const ownSuccess = event.origin === window.location.origin && data.action === 'kik:quote-submitted';
      if (!jotformSuccess && !ownSuccess) return;
      if (data.formID && String(data.formID) !== this.activeUrl.pathname.split('/')[1]) return;
      this.submitted = true;
      clearTimeout(this.loadTimeout);
      this.querySelector('[data-quote-loading]').hidden = true;
      this.querySelector('[data-quote-body]').setAttribute('aria-busy', 'false');
      this.querySelector('[data-quote-frame]').hidden = true;
      this.querySelector('[data-quote-success]').hidden = false;
      if (this.dialog.open) this.querySelector('[data-quote-success-title]').focus({ preventScroll: true });
      this.track('quote_form_submit_success');
    }

    track(eventName) {
      if (!window.kikLeadTracking?.enabled) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        source: 'pdp_quote_modal',
        product_id: this.dataset.productId,
        form_id: this.activeUrl.pathname.split('/')[1],
        quote_type: this.quoteKind,
        sku: this.activeUrl.searchParams.get('sku') || '',
        color: this.activeUrl.searchParams.get('color') || ''
      });
    }
  }

  customElements.define('kik-quote-modal', KikQuoteModal);
})();
